/**
 * Traffic Light System (NAU-247)
 *
 * A single status vocabulary that drives the dashboard tile colours, the
 * Today's-Status rollup and the per-domain badges. This is the single source
 * of truth for the rule — when the backend `dashboard` aggregation query is
 * built, this derivation should move there (and this stays as the FE mirror).
 */

export type DashboardStatus = "ON_TRACK" | "NEEDS_ATTENTION" | "ACTION";

export type DashboardBadgeVariant = "success" | "warning" | "error";

interface StatusMeta {
  /** Human label shown in pills / rollups */
  label: string;
  /** Maps onto the subframe Badge variant */
  badgeVariant: DashboardBadgeVariant;
  /** CSS custom-property reference, for SVG strokes and accents */
  colorVar: string;
  /** Subtle background tint (traffic-light system) for cards */
  tintClass: string;
  /** Border tint to pair with the background */
  borderClass: string;
  /** Text colour class */
  textClass: string;
}

export const DASHBOARD_STATUS_META: Record<DashboardStatus, StatusMeta> = {
  ON_TRACK: {
    label: "On track",
    badgeVariant: "success",
    colorVar: "var(--color-success-600)",
    tintClass: "bg-success-50",
    borderClass: "border-success-100",
    textClass: "text-success-700",
  },
  NEEDS_ATTENTION: {
    label: "Needs attention",
    badgeVariant: "warning",
    colorVar: "var(--color-warning-600)",
    tintClass: "bg-warning-50",
    borderClass: "border-warning-100",
    textClass: "text-warning-800",
  },
  ACTION: {
    label: "Action required",
    badgeVariant: "error",
    colorVar: "var(--color-error-500)",
    tintClass: "bg-error-50",
    borderClass: "border-error-100",
    textClass: "text-error-700",
  },
};

/**
 * The "due soon" window for the amber state. Per the NAU-247 spec this is 3
 * days. Centralised here so a product change is a one-line edit.
 */
export const DUE_SOON_DAYS = 3;

export interface StatusCounts {
  /** Overdue / missed / unresolved items (drives the red ACTION state) */
  overdue: number;
  /**
   * Items due within DUE_SOON_DAYS that are NOT yet late (drives amber). The
   * caller is responsible for excluding "checklists not yet due" per the spec.
   */
  dueSoon: number;
}

/**
 * Traffic-light rule:
 *  - ACTION (red):           anything overdue / missed / unresolved
 *  - NEEDS_ATTENTION (amber): nothing late, but something due within the window
 *  - ON_TRACK (green):       all complete, nothing overdue or imminent
 */
export function deriveStatus({
  overdue,
  dueSoon,
}: StatusCounts): DashboardStatus {
  if (overdue > 0) return "ACTION";
  if (dueSoon > 0) return "NEEDS_ATTENTION";
  return "ON_TRACK";
}

/** Build two initials from a name, e.g. "Jane Wilson" -> "JW" */
export function getInitials(name: string): string {
  return (
    name
      .split(" ")
      .map((n) => n[0])
      .filter(Boolean)
      .join("")
      .toUpperCase()
      .slice(0, 2) || "?"
  );
}
